import {boundMethod} from "autobind-decorator";
import React from "react";
import {Titled} from "react-titled";

import display from "@/services/display-name";
import {ERoles} from "@/services/models";
import session from "@/services/session";
import {injectIntl, IntlShape} from "react-intl";
import {IUserDetails} from "./models";

import ContainerCol from "@toolbox/display-blocks/ContainerCol";
import Loading from "@toolbox/render-page/Loading";
import NotFound from "@toolbox/render-page/NotFound";
import T, {intl2Str} from "@translate/T";
import AccountSettings from "./AccountSettings";
import RolesTable from "./RolesTable";

interface IAccountProps {
    intl: IntlShape;
}

interface IAccountState {
    userDetail?: IUserDetails;
    isNotFound: boolean;
}

class Account extends React.PureComponent<IAccountProps, IAccountState> {
    public readonly state: IAccountState = {
        isNotFound: false,
    };

    public async componentDidMount() {
        await this.retrieveUser(session.user ?? "");
    }

    @boundMethod
    public onChange(changes: Partial<IUserDetails>) {
        const {userDetail} = this.state;
        if (!userDetail) {
            return;
        }

        this.setState({userDetail: {...userDetail, ...changes}});
    }

    @boundMethod
    public getTitle(parent: string) {
        return intl2Str(this.props.intl, "Account settings") + " | " + parent;
    }

    public render() {
        const {isNotFound, userDetail} = this.state;

        if (isNotFound) {
            return (
                <NotFound id={session.user} item={<T>The specified user</T>} />
            );
        }

        if (!userDetail) {
            return <Loading />;
        }

        return (
            <Titled title={this.getTitle}>
                <ContainerCol col={8}>
                    <h3>
                        <T>Account settings</T>
                    </h3>
                    <hr />

                    <AccountSettings
                        user={session.user ?? ""}
                        redirectUrl=""
                        userDetail={userDetail}
                        onUserChance={this.onChange}
                    >
                        {this.renderExtraContent(userDetail)}
                    </AccountSettings>
                </ContainerCol>
            </Titled>
        );
    }

    private renderExtraContent(userDetail: IUserDetails) {
        const groupsUrl = session.hasRole(ERoles.Administrator)
            ? "admin/groups"
            : undefined;

        return (
            <RolesTable
                roles={userDetail.roles}
                groups={userDetail.groups}
                projects={userDetail.projects}
                groupsUrl={groupsUrl}
            />
        );
    }

    private async retrieveUser(username: string) {
        const userDetail = await display.getUserDetails(username);

        if (!userDetail) {
            this.setState({isNotFound: true});
            return;
        }

        this.setState({userDetail});
    }
}

export default injectIntl(Account);
